import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import { useCitations } from 'shared/panda/citations/citations.hooks';
import type { CitationQuoteView } from 'shared/panda/citations/citations.hooks';
import { useLang } from 'shared/hooks/lang.hooks';
import type { HubSource } from 'shared/panda/agent/ag-ui.constants';
import { HubOrgBadge } from '../hub-org-badge';
import styles from './citations.module.scss';

// ~2 quote-box lines at the panel's width; longer quotes get "See more".
const QUOTE_PREVIEW_LENGTH = 160;

interface CitationsProps {
  chunkIds: string[];
  /** Fired when the section is expanded; collapsing is not reported. */
  onOpen: () => void;
  /** Fired with the quote's 1-based position and its new state. */
  onToggleQuote: (position: number, expanded: boolean) => void;
  focusable?: boolean;
  /** Hub answer: the source whose org badge shows alongside the citations toggle. */
  hubSource?: HubSource;
}

interface CitationQuoteProps extends CitationQuoteView {
  /** Fired with the new state on every "See more" / "See less". */
  onToggle: (expanded: boolean) => void;
  focusable?: boolean;
}

/**
 * Collapsible "View Citations (N)" row for an answer card. Each cited chunk id
 * is fetched from zeus (cached per id); only ids that resolve to a real chunk
 * are counted and shown, so a hallucinated id never inflates the count and an
 * answer whose ids all fail to resolve renders nothing.
 */
export function Citations({
  chunkIds,
  onOpen,
  onToggleQuote,
  focusable = true,
  hubSource,
}: CitationsProps) {
  const lang = useLang('AI_CHAT');
  const { quotes, label, expanded, toggleExpanded } = useCitations(
    chunkIds,
    lang,
    QUOTE_PREVIEW_LENGTH,
  );

  if (quotes.length === 0) return null;

  const handleToggle = () => {
    if (!expanded) onOpen();
    toggleExpanded();
  };

  return (
    <>
      <div className={styles.toggleRow}>
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={expanded}
          tabIndex={focusable ? undefined : -1}
          onClick={handleToggle}>
          {label}
          <FontAwesomeIcon
            icon={expanded ? faChevronUp : faChevronDown}
            className={styles.chevron}
          />
        </button>
        {hubSource && <HubOrgBadge source={hubSource} />}
      </div>
      {expanded && (
        <div className={styles.quotes}>
          {quotes.map((quote, index) => (
            <CitationQuote
              key={quote.id}
              {...quote}
              focusable={focusable}
              onToggle={quoteExpanded => onToggleQuote(index + 1, quoteExpanded)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function CitationQuote({ text, preview, onToggle, focusable = true }: CitationQuoteProps) {
  const lang = useLang('AI_CHAT');
  const [showFull, setShowFull] = useState(false);

  if (preview == null) {
    return <blockquote className={styles.quote}>“{text}”</blockquote>;
  }

  const toggleFull = () => {
    setShowFull(value => !value);
    onToggle(!showFull);
  };

  return (
    <blockquote className={styles.quote}>
      {showFull ? `“${text}” ` : `“${preview}... `}
      <button
        type="button"
        className={styles.seeMore}
        tabIndex={focusable ? undefined : -1}
        onClick={toggleFull}>
        {showFull ? lang.PANDA_SEE_LESS || 'See less' : lang.PANDA_SEE_MORE || 'See more'}
      </button>
    </blockquote>
  );
}
